import { Controller } from "@hotwired/stimulus"

type ModelRow = {
  id: string
  display_name: string
  slug: string
  family: string | null
  creator_organization: string | null
  trueskill_mu: number | null
  trueskill_sigma: number | null
  in_competition_count?: number
}

type Payload = {
  mode: string
  standard_set_ids?: string[]
  in_competition_ids?: string[]
  selected_ids?: string[]
  models: ModelRow[]
}

export default class ModelPickerController extends Controller {
  static targets = [
    "dialog", "searchInput", "list", "selectedCount", "submitButton",
    "addStandardSetButton", "addTopRankedButton", "addAllMissingButton",
    "editStandardSetButton", "editStandardSetSection", "saveStandardSetButton",
    "selectAllButton", "title", "modeLabel"
  ]
  static values = {
    mode: String,
    competitionId: String,
    fetchUrl: String,
    saveStandardSetUrl: String,
    submitUrl: String,
    submitToken: String,
    submitMode: { type: String, default: "post" }, // "post" | "inline"
    showCounts: { type: Boolean, default: true },
    selectedIds: { type: Array, default: [] as string[] },
    defaultCount: { type: Number, default: 1 }
  }

  declare dialogTarget: HTMLDialogElement
  declare searchInputTarget: HTMLInputElement
  declare listTarget: HTMLElement
  declare selectedCountTarget: HTMLElement
  declare submitButtonTarget: HTMLButtonElement
  declare addStandardSetButtonTarget: HTMLButtonElement
  declare hasAddStandardSetButtonTarget: boolean
  declare addTopRankedButtonTarget: HTMLButtonElement
  declare hasAddTopRankedButtonTarget: boolean
  declare addAllMissingButtonTarget: HTMLButtonElement
  declare hasAddAllMissingButtonTarget: boolean
  declare editStandardSetButtonTarget: HTMLButtonElement
  declare hasEditStandardSetButtonTarget: boolean
  declare editStandardSetSectionTarget: HTMLElement
  declare hasEditStandardSetSectionTarget: boolean
  declare saveStandardSetButtonTarget: HTMLButtonElement
  declare hasSaveStandardSetButtonTarget: boolean
  declare selectAllButtonTarget: HTMLButtonElement
  declare hasSelectAllButtonTarget: boolean
  declare titleTarget: HTMLElement
  declare hasTitleTarget: boolean
  declare modeLabelTarget: HTMLElement
  declare hasModeLabelTarget: boolean
  declare modeValue: string
  declare competitionIdValue: string
  declare fetchUrlValue: string
  declare saveStandardSetUrlValue: string
  declare submitUrlValue: string
  declare submitTokenValue: string
  declare submitModeValue: string
  declare showCountsValue: boolean
  declare selectedIdsValue: string[]
  declare defaultCountValue: number

  private payload: Payload | null = null
  private selectedIds: Set<string> = new Set()
  private rowCounts: Map<string, number> = new Map()
  private editingStandardSet = false
  private query = ""

  modeValueChanged(_current: string, previous: string) {
    if (previous === undefined) return
    this.payload = null
    this.selectedIds.clear()
    this.rowCounts.clear()
  }

  open(event?: Event) {
    event?.preventDefault()
    const show = () => {
      this.seedSelectionFromValue()
      this.render()
      this.dialogTarget.showModal()
    }
    if (!this.payload) {
      this.fetchPayload().then(show)
    } else {
      show()
    }
  }

  close() {
    this.dialogTarget.close()
    this.selectedIds.clear()
    this.rowCounts.clear()
    this.editingStandardSet = false
    this.render()
  }

  cancelOrClose() {
    if (this.editingStandardSet) {
      this.exitEditStandardSet()
    } else {
      this.close()
    }
  }

  onSearch() {
    this.query = this.searchInputTarget.value.trim().toLowerCase()
    this.renderList()
  }

  selectAllVisible() {
    if (!this.payload) return
    const visible = this.filteredModels().map(m => m.id)
    if (visible.length === 0) return
    const allSelected = visible.every(id => this.selectedIds.has(id))
    if (allSelected) {
      visible.forEach(id => this.selectedIds.delete(id))
    } else {
      visible.forEach(id => {
        this.selectedIds.add(id)
        this.ensureRowCount(id)
      })
    }
    this.render()
  }

  onRowToggle(event: Event) {
    const cb = event.currentTarget as HTMLInputElement
    const id = cb.dataset.modelId!
    if (cb.checked) {
      this.selectedIds.add(id)
      this.ensureRowCount(id)
    } else {
      this.selectedIds.delete(id)
    }
    this.renderList()
    this.renderFooter()
  }

  onCountChange(event: Event) {
    const input = event.currentTarget as HTMLInputElement
    const id = input.dataset.modelId!
    const value = Math.max(1, Math.min(20, parseInt(input.value, 10) || 1))
    this.rowCounts.set(id, value)
    if (input.value !== String(value)) input.value = String(value)
  }

  private seedSelectionFromValue() {
    if (this.selectedIdsValue.length === 0) return
    this.selectedIds = new Set(this.selectedIdsValue)
  }

  private ensureRowCount(id: string) {
    if (!this.showCountsValue) return
    if (this.rowCounts.has(id)) return
    if (!this.payload) return
    const m = this.payload.models.find(x => x.id === id)
    if (!m) return
    this.rowCounts.set(id, this.defaultCountFor(m))
  }

  private defaultCountFor(m: ModelRow): number {
    if (this.payload?.in_competition_ids?.includes(m.id)) return 1
    return this.defaultCountValue || 1
  }

  private async fetchPayload() {
    const params = new URLSearchParams()
    if (this.competitionIdValue) params.set("competition_id", this.competitionIdValue)
    if (this.modeValue) params.set("mode", this.modeValue)
    const sep = this.fetchUrlValue.includes("?") ? "&" : "?"
    const url = params.toString() ? `${this.fetchUrlValue}${sep}${params.toString()}` : this.fetchUrlValue
    const res = await fetch(url, { headers: { "Accept": "application/json" } })
    this.payload = await res.json()
  }

  private render() {
    this.renderList()
    this.renderFooter()
  }

  private renderList() {
    if (!this.payload) return
    const filtered = this.filteredModels()
    const sorted = this.sortModels(filtered)

    this.listTarget.innerHTML = sorted.map(m => this.rowHtml(m)).join("")
    this.listTarget.querySelectorAll<HTMLInputElement>("input[type=checkbox][data-model-id]").forEach(cb => {
      cb.addEventListener("change", e => this.onRowToggle(e))
    })
    this.listTarget.querySelectorAll<HTMLInputElement>("input[type=number][data-model-id]").forEach(input => {
      input.addEventListener("change", e => this.onCountChange(e))
      input.addEventListener("click", e => e.stopPropagation())
    })

    if (this.hasSelectAllButtonTarget) {
      const visible = filtered.map(m => m.id)
      const allSelected = visible.length > 0 && visible.every(id => this.selectedIds.has(id))
      this.selectAllButtonTarget.textContent = allSelected ? "Deselect all" : "Select all"
      this.selectAllButtonTarget.disabled = visible.length === 0 || this.editingStandardSet
    }
  }

  private renderFooter() {
    const n = this.selectedIds.size
    this.selectedCountTarget.textContent = `${n} selected`
    this.submitButtonTarget.disabled = n === 0 && !this.editingStandardSet
    if (this.submitModeValue === "inline") {
      this.submitButtonTarget.textContent = "Set models"
    } else {
      this.submitButtonTarget.textContent = n === 1 ? "Add 1 model" : `Add ${n} models`
    }
  }

  private filteredModels(): ModelRow[] {
    if (!this.payload) return []
    if (!this.query) return this.payload.models
    return this.payload.models.filter(m =>
      m.display_name.toLowerCase().includes(this.query) ||
      m.slug.toLowerCase().includes(this.query) ||
      (m.family ?? "").toLowerCase().includes(this.query) ||
      (m.creator_organization ?? "").toLowerCase().includes(this.query)
    )
  }

  private sortModels(models: ModelRow[]): ModelRow[] {
    return [...models].sort((a, b) => {
      const aRated = a.trueskill_mu != null
      const bRated = b.trueskill_mu != null
      if (aRated !== bRated) return aRated ? -1 : 1
      if (aRated && bRated && a.trueskill_mu !== b.trueskill_mu) {
        return (b.trueskill_mu! - a.trueskill_mu!)
      }
      return a.display_name.localeCompare(b.display_name)
    })
  }

  private rowHtml(m: ModelRow): string {
    const inCompIds = this.payload!.in_competition_ids ?? []
    const inComp = inCompIds.includes(m.id)
    const isSelected = this.selectedIds.has(m.id)
    const checked = isSelected ? "checked" : ""
    const dimClass = inComp ? "opacity-60" : ""
    const mu = m.trueskill_mu == null ? "—" : `μ ${m.trueskill_mu.toFixed(1)}`
    const sigma = m.trueskill_sigma == null ? "" : `σ ${m.trueskill_sigma.toFixed(1)}`
    const inBadge = inComp
      ? `<span class="badge badge-sm badge-ghost">in (${m.in_competition_count ?? 0})</span>`
      : ""

    const showCount = this.showCountsValue && isSelected && !this.editingStandardSet
    const countValue = this.rowCounts.get(m.id) ?? this.defaultCountFor(m)
    const countInput = showCount
      ? `<input type="number" min="1" max="20" value="${countValue}"
                class="input input-bordered input-xs w-14 tabular-nums"
                data-model-id="${m.id}"
                title="Number of generations to add"
                aria-label="Generations to add for ${escapeHtml(m.display_name)}">`
      : ""

    return `
      <li class="flex items-center gap-3 py-2 ${dimClass}">
        <input type="checkbox" class="checkbox checkbox-sm checkbox-primary"
               data-model-id="${m.id}" ${checked}>
        <div class="flex-1 min-w-0">
          <div class="font-medium text-sm truncate">${escapeHtml(m.display_name)}</div>
          <div class="text-xs text-base-content/60 truncate">
            ${escapeHtml(m.creator_organization ?? "")} · ${escapeHtml(m.family ?? "")}
          </div>
        </div>
        <div class="text-right text-xs tabular-nums" title="TrueSkill rating for this mode. μ = skill estimate (higher is better); σ = uncertainty (lower = more confident). Displayed rank uses μ − 3σ.">
          <div>${mu}</div>
          <div class="text-base-content/60">${sigma}</div>
        </div>
        ${inBadge}
        ${countInput}
      </li>
    `
  }

  toggleStandardSet(event: Event) { this.applyMacro(this.payload?.standard_set_ids ?? [], event) }

  toggleTopRanked(event: Event) {
    if (!this.payload) return
    const top = [...this.payload.models]
      .filter(m => m.trueskill_mu != null)
      .sort((a, b) => b.trueskill_mu! - a.trueskill_mu!)
      .slice(0, 10)
      .map(m => m.id)
    this.applyMacro(top, event)
  }

  toggleAllMissing(event: Event) {
    if (!this.payload) return
    const inCompIds = this.payload.in_competition_ids ?? []
    const missing = this.payload.models
      .filter(m => !inCompIds.includes(m.id))
      .map(m => m.id)
    this.applyMacro(missing, event)
  }

  submitSelection() {
    const ids = [...this.selectedIds]
    if (ids.length === 0) return

    if (this.submitModeValue === "inline") {
      const models = ids
        .map(id => this.payload?.models.find(m => m.id === id))
        .filter((m): m is ModelRow => !!m)
        .map(m => ({ id: m.id, slug: m.slug, display_name: m.display_name }))
      const slugs = models.map(m => m.slug)
      this.dispatch("selected", { detail: { ids, slugs, models } })
      this.close()
      return
    }

    const form = document.createElement("form")
    form.method = "post"
    form.action = this.submitUrlValue

    const csrf = document.createElement("input")
    csrf.type = "hidden"
    csrf.name = "authenticity_token"
    csrf.value = this.submitTokenValue
    form.appendChild(csrf)

    ids.forEach(id => {
      const idInput = document.createElement("input")
      idInput.type = "hidden"
      idInput.name = "model_ids[]"
      idInput.value = id
      form.appendChild(idInput)

      if (this.showCountsValue) {
        const m = this.payload?.models.find(x => x.id === id)
        const count = this.rowCounts.get(id) ?? (m ? this.defaultCountFor(m) : 1)
        const countInput = document.createElement("input")
        countInput.type = "hidden"
        countInput.name = `model_counts[${id}]`
        countInput.value = String(count)
        form.appendChild(countInput)
      }
    })

    document.body.appendChild(form)
    form.submit()
  }

  private applyMacro(macroIds: string[], event: Event) {
    const replace = (event as MouseEvent).altKey
    if (replace) this.selectedIds.clear()

    const visibleSet = new Set(this.filteredModels().map(m => m.id))
    const targetIds = macroIds.filter(id => visibleSet.has(id))

    const allSelectedAlready = targetIds.length > 0 && targetIds.every(id => this.selectedIds.has(id))
    if (allSelectedAlready && !replace) {
      targetIds.forEach(id => this.selectedIds.delete(id))
    } else {
      targetIds.forEach(id => this.selectedIds.add(id))
    }

    this.render()
  }

  enterEditStandardSet() {
    if (!this.payload) return
    this.editingStandardSet = true
    this.selectedIds = new Set(this.payload.standard_set_ids ?? [])
    if (this.hasEditStandardSetSectionTarget) this.editStandardSetSectionTarget.hidden = false
    this.submitButtonTarget.classList.add("hidden")
    if (this.hasSaveStandardSetButtonTarget) this.saveStandardSetButtonTarget.classList.remove("hidden")
    if (this.hasAddStandardSetButtonTarget) this.addStandardSetButtonTarget.disabled = true
    if (this.hasAddTopRankedButtonTarget) this.addTopRankedButtonTarget.disabled = true
    if (this.hasAddAllMissingButtonTarget) this.addAllMissingButtonTarget.disabled = true
    if (this.hasEditStandardSetButtonTarget) this.editStandardSetButtonTarget.disabled = true
    if (this.hasTitleTarget) this.titleTarget.textContent = "Editing standard set"
    this.render()
  }

  exitEditStandardSet() {
    this.editingStandardSet = false
    if (this.hasEditStandardSetSectionTarget) this.editStandardSetSectionTarget.hidden = true
    this.submitButtonTarget.classList.remove("hidden")
    if (this.hasSaveStandardSetButtonTarget) this.saveStandardSetButtonTarget.classList.add("hidden")
    if (this.hasAddStandardSetButtonTarget) this.addStandardSetButtonTarget.disabled = false
    if (this.hasAddTopRankedButtonTarget) this.addTopRankedButtonTarget.disabled = false
    if (this.hasAddAllMissingButtonTarget) this.addAllMissingButtonTarget.disabled = false
    if (this.hasEditStandardSetButtonTarget) this.editStandardSetButtonTarget.disabled = false
    if (this.hasTitleTarget && this.hasModeLabelTarget) {
      this.titleTarget.textContent = `Add models — ${this.modeLabelTarget.textContent}`
    }
    this.selectedIds.clear()
    this.seedSelectionFromValue()
    this.render()
  }

  async saveStandardSet() {
    const ids = [...this.selectedIds]
    const res = await fetch(this.saveStandardSetUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.submitTokenValue
      },
      body: JSON.stringify({ model_ids: ids })
    })
    if (!res.ok) {
      alert("Failed to save standard set")
      return
    }
    if (this.payload) this.payload.standard_set_ids = ids
    this.exitEditStandardSet()
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!)
  )
}
